import { bigQuery, mixpanel } from '@fiverr-private/obs';
import { BusinessType } from '../../hooks/useBusinessDetailsData/getBusinessDetails.types';
import { UserRole } from '../../types';
import { getBaseBqFields, getBaseClickBqFields, getProgram } from './bq';
import { InlineHelpCtaType, InlineHelpMenuItemType } from './types';

interface BiProps {
    bsmName: string | null;
    businessType: BusinessType;
    eventType?: string;
    componentName?: string;
    source?: string;
    elementText?: string;
}

const HIGH_TOUCH_MENU_COMPONENT_NAME = 'high_touch_menu';
const PRO_LIHP_SOURCE = 'business_lihp';

/**
 * User clicked on the inline help component, whether it's a dropdown
 * menu trigger, or a let's chat CTA
 */
export const reportClickedOnInlineHelp = ({
    source,
    businessType,
    bsmName,
    cta,
    userRole,
}: BiProps & { cta: InlineHelpCtaType; userRole: UserRole }) => {
    bigQuery.send({
        ...getBaseClickBqFields(),
        type: 'clicked_on_bsm_banner',
        page: {
            name: source,
            component: {
                name: 'inline_help',
                displayed_state: bsmName,
            },
        },
        program: {
            id: getProgram(businessType),
        },
    });

    const mixpanelEventName = source?.startsWith(PRO_LIHP_SOURCE)
        ? 'Clicked on Dashboard Component'
        : 'Clicked on High Touch Component';

    mixpanel.track(mixpanelEventName, {
        'Component Name': 'high_touch_menu',
        'Clicked Element': cta,
        'Client Segment': businessType,
        'User Role': userRole,
    });
};

/**
 * user clicked on send message or schedule meeting
 * in the inline help menu
 */
export const reportClickedOnContactBsm = ({
    elementText,
    source,
    businessType,
    bsmName,
    userRole,
}: BiProps & { elementText: InlineHelpMenuItemType; userRole: UserRole }) => {
    bigQuery.send({
        ...getBaseClickBqFields(),
        type: 'clicked_on_contact_bsm',
        page: {
            name: source,
            component: {
                name: 'contact_bsm',
                displayed_state: bsmName,
            },
            element: {
                text: elementText,
            },
        },
        program: {
            id: getProgram(businessType),
        },
    });

    mixpanel.track('Selected a menu option', {
        'Clicked Element': elementText,
        'Client Segment': businessType,
        'User Role': userRole,
        'Component Name': HIGH_TOUCH_MENU_COMPONENT_NAME,
    });
};

/**
 * user clicked on send message or schedule a meeting in the header
 */
export const reportClickedOnHeaderContact = ({
    businessType,
    elementText,
    bsmName,
    action,
}: BiProps & {
    action: InlineHelpMenuItemType;
}) => {
    const common = {
        ...getBaseClickBqFields(),
        event_source: 'help',
        action: {
            type: 'click',
        },
        program: {
            id: getProgram(businessType),
        },
        page: {
            component: {
                name: 'header',
                displayed_state: bsmName,
            },
            element: {
                text: elementText,
            },
        },
    };

    if (action === 'send_message') {
        bigQuery.send({
            ...common,
            type: 'clicked_on_fiverr_pro_bsm_send_message',
        });
    } else if (action === 'schedule_meeting') {
        bigQuery.send({
            ...common,
            type: 'clicked_on_fiverr_pro_bsm_schedule_a_meeting',
        });
    }
};

/**
 * user sent a message, whether from header or inline help menu
 */
export const reportMessageSent = ({
    source,
    businessType,
    elementText,
    bsmName,
    userRole,
}: BiProps & { userRole: UserRole }) => {
    bigQuery.send({
        ...getBaseClickBqFields(),
        type: 'clicked_on_fiverr_pro_bsm_pop_up_send_message',
        action: {
            type: 'click',
        },
        event_source: 'help',
        program: {
            id: getProgram(businessType),
        },
        page: {
            component: {
                name: source,
                displayed_state: bsmName,
            },
            element: {
                text: elementText,
            },
        },
    });

    mixpanel.track('Clicked on Send a message', {
        'User Role': userRole,
        'Client Segment': businessType,
        'Clicked Element': elementText,
        'Component Name': 'send_message_modal',
    });
};

/**
 * user clicked the help icon in the header
 */
export const reportClickedMenuButton = ({
    eventType,
    componentName,
    businessType,
    bsmName,
}: Pick<BiProps, 'eventType' | 'componentName' | 'businessType' | 'bsmName'>) => {
    bigQuery.send({
        ...getBaseClickBqFields(),
        type: eventType,
        program: {
            id: getProgram(businessType),
        },
        page: {
            component: {
                name: componentName,
                displayed_state: bsmName,
            },
        },
    });
};

/**
 * user clicked one of the other (header) help menu items
 */
export const reportHelpMenuItemClicked = ({
    elementText,
    type,
}: {
    elementText: string;
    type:
        | 'clicked_on_fiverr_pro_help_menu_faqs'
        | 'clicked_on_fiverr_pro_help_menu_help_center'
        | 'clicked_on_fiverr_pro_help_menu_live_chat';
}) => {
    bigQuery.send({
        ...getBaseClickBqFields(),
        type,
        action: {
            type: 'click',
        },
        page: {
            component: {
                name: 'header',
            },
            element: {
                text: elementText,
            },
        },
    });
};

/**
 * Header help menu impression
 */
export const reportHelpMenuIsShown = ({
    eventType,
    componentName,
    businessType,
    bsmName,
}: Pick<BiProps, 'eventType' | 'componentName' | 'businessType' | 'bsmName'>) => {
    bigQuery.send({
        ...getBaseBqFields(),
        group: 'user_impressions',
        type: eventType, // 'fiverr_pro_help_menu_is_shown',
        page: {
            component: {
                name: componentName,
                displayed_state: bsmName,
            },
        },
        program: {
            id: getProgram(businessType),
        },
    });
};

/**
 * Inline help menu impression
 */
export const reportInlineHelpMenuIsShown = ({
    businessType,
    bsmName,
    source,
    userRole,
}: Omit<BiProps, 'elementText'> & { userRole: UserRole }) => {
    bigQuery.send({
        ...getBaseBqFields(),
        group: 'user_impressions',
        type: 'bsm_banner_is_shown',
        page: {
            name: source,
            component: {
                name: 'inline_help',
                displayed_state: bsmName,
            },
        },
        program: {
            id: getProgram(businessType),
        },
    });

    const mixpanelEventName = source?.startsWith(PRO_LIHP_SOURCE)
        ? 'Viewed Dashboard Component'
        : 'Viewed High Touch Component';

    mixpanel.track(mixpanelEventName, {
        'Component Name': HIGH_TOUCH_MENU_COMPONENT_NAME,
        'Client Segment': businessType,
        'User Role': userRole,
    });
};
